import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import DynamicStepper from '../../Shared/DynamicStepper/DynamicStepper';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle, faCheckCircle, faEye, faDownload, faFilePowerpoint, faWandMagicSparkles, faLayerGroup, faRobot, faStar, faArrowRight, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { useNavigate, useLocation } from 'react-router-dom';
import ApiServices from '../../Services/ApiServices';
import { toastHelper } from '../../../Helpers/toastHelper';
import { AppKeys } from '../../Shared/AppKeys';
import PowerPointViewer from '../../Shared/PowerPointViewer/PowerPointViewer';
import { useLanguage } from '../../../contexts/LanguageContext';
import { getPublicUrlFromPhysicalPath } from '../../../Helpers/UrlHelpers';
import './StepperDemo.css';

// Step 1: Basic Info
const BasicInfoStep = ({ stepData, updateData }) => {
  const { t } = useLanguage();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', paddingTop: '1rem' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        <label style={{ fontWeight: 'bold', color: '#334155' }}>
          {t('pptx_topic')}
        </label>
        <input 
          type="text" 
          style={{ padding: '0.8rem', borderRadius: '12px', border: '1px solid #cbd5e1', outline: 'none', fontFamily: 'inherit' }} 
          placeholder={t('pptx_topicPlaceholder')}
          value={stepData.topic || ''}
          onChange={(e) => updateData({ topic: e.target.value })}
        />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        <label style={{ fontWeight: 'bold', color: '#334155' }}>
          {t('pptx_grade')}
        </label>
        <input 
          type="text" 
          style={{ padding: '0.8rem', borderRadius: '12px', border: '1px solid #cbd5e1', outline: 'none', fontFamily: 'inherit' }} 
          placeholder={t('pptx_gradePlaceholder')}
          value={stepData.grade_level || ''}
          onChange={(e) => updateData({ grade_level: e.target.value })}
        />
      </div>
    </div>
  );
};

// Step 2: Configuration
const ConfigStep = ({ stepData, updateData }) => {
  const { t } = useLanguage();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', paddingTop: '1rem' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        <label style={{ fontWeight: 'bold', color: '#334155' }}>
          {t('pptx_slidesCount')}
        </label>
        <input 
          type="number" 
          min="1"
          max="10"
          style={{ padding: '0.8rem', borderRadius: '12px', border: '1px solid #cbd5e1', outline: 'none', fontFamily: 'inherit' }} 
          value={stepData.slides_count || 10}
          onChange={(e) => updateData({ slides_count: parseInt(e.target.value) || 1 })}
        />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        <label style={{ fontWeight: 'bold', color: '#334155' }}>
          {t('pptx_lang')}
        </label>
        <select 
          style={{ padding: '0.8rem', borderRadius: '12px', border: '1px solid #cbd5e1', outline: 'none', fontFamily: 'inherit' }}
          value={stepData.language || 'ar'}
          onChange={(e) => updateData({ language: e.target.value })}
        >
          <option value="ar">{t('pptx_langAr')}</option>
          <option value="en">{t('pptx_langEn')}</option>
        </select>
      </div>
    </div>
  );
};

// Step 3: Confirmation
const ConfirmationStep = ({ stepData, updateData }) => {
  const { t } = useLanguage();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', paddingTop: '1rem' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        <label style={{ fontWeight: 'bold', color: '#334155' }}>
          {t('pptx_notes')}
        </label>
        <textarea 
          rows="4"
          style={{ padding: '0.8rem', borderRadius: '12px', border: '1px solid #cbd5e1', outline: 'none', resize: 'vertical', fontFamily: 'inherit' }} 
          placeholder={t('pptx_notesPlaceholder')}
          value={stepData.additional_notes || ''}
          onChange={(e) => updateData({ additional_notes: e.target.value })}
        />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', marginTop: '1rem' }}>
        <FontAwesomeIcon icon={faCheckCircle} size="3x" color="#10b981" />
        <h4 style={{ margin: 0, color: '#334155' }}>
          {t('pptx_readyTitle')}
        </h4>
        <p style={{ margin: 0, color: '#64748b', textAlign: 'center', fontSize: '0.9rem' }}>
          {t('pptx_readyDesc')}
        </p>
      </div>
    </div>
  );
};

// --- AI Enhanced Variants ---
const AIBasicInfoStep = ({ stepData, updateData }) => {
  const { t, language } = useLanguage();
  return (
    <motion.div 
      initial={{ opacity: 0, x: language === 'ar' ? -50 : 50 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.5, type: 'spring' }}
      style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', paddingTop: '1rem' }}
    >
      <motion.div 
        whileHover={{ scale: 1.02 }}
        style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', position: 'relative' }}
      >
        <label style={{ fontWeight: 'bold', color: '#e2e8f0', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <FontAwesomeIcon icon={faWandMagicSparkles} color="#a855f7" />
          {t('pptx_topic')}
        </label>
        <input 
          type="text" 
          style={{ padding: '0.8rem', borderRadius: '12px', border: '1px solid rgba(99, 102, 241, 0.3)', outline: 'none', fontFamily: 'inherit', background: 'rgba(30, 41, 59, 0.5)', color: '#fff' }} 
          placeholder={t('pptx_topicPlaceholder')}
          value={stepData.topic || ''}
          onChange={(e) => updateData({ topic: e.target.value })}
        />
      </motion.div>
      <motion.div 
        whileHover={{ scale: 1.02 }}
        style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', position: 'relative' }}
      >
        <label style={{ fontWeight: 'bold', color: '#e2e8f0', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <FontAwesomeIcon icon={faLayerGroup} color="#ec4899" />
          {t('pptx_grade')}
        </label>
        <input 
          type="text" 
          style={{ padding: '0.8rem', borderRadius: '12px', border: '1px solid rgba(99, 102, 241, 0.3)', outline: 'none', fontFamily: 'inherit', background: 'rgba(30, 41, 59, 0.5)', color: '#fff' }} 
          placeholder={t('pptx_gradePlaceholder')}
          value={stepData.grade_level || ''}
          onChange={(e) => updateData({ grade_level: e.target.value })}
        />
      </motion.div>
    </motion.div>
  );
};

const AIConfigStep = ({ stepData, updateData }) => {
  const { t } = useLanguage();
  return (
    <motion.div 
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5, type: 'spring' }}
      style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', paddingTop: '1rem' }}
    >
      <motion.div 
        whileHover={{ scale: 1.02 }}
        style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}
      >
        <label style={{ fontWeight: 'bold', color: '#e2e8f0' }}>
          {t('pptx_slidesCount')}
        </label>
        <input 
          type="number" 
          min="1"
          max="10"
          style={{ padding: '0.8rem', borderRadius: '12px', border: '1px solid rgba(99, 102, 241, 0.3)', outline: 'none', fontFamily: 'inherit', background: 'rgba(30, 41, 59, 0.5)', color: '#fff' }} 
          value={stepData.slides_count || 10}
          onChange={(e) => updateData({ slides_count: parseInt(e.target.value) || 1 })}
        />
      </motion.div>
      <motion.div 
        whileHover={{ scale: 1.02 }}
        style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}
      >
        <label style={{ fontWeight: 'bold', color: '#e2e8f0' }}>
          {t('pptx_lang')}
        </label>
        <select 
          style={{ padding: '0.8rem', borderRadius: '12px', border: '1px solid rgba(99, 102, 241, 0.3)', outline: 'none', fontFamily: 'inherit', background: 'rgba(30, 41, 59, 0.5)', color: '#fff' }}
          value={stepData.language || 'ar'}
          onChange={(e) => updateData({ language: e.target.value })}
        >
          <option value="ar">{t('pptx_langAr')}</option>
          <option value="en">{t('pptx_langEn')}</option>
        </select>
      </motion.div>
    </motion.div>
  );
};

const AIConfirmationStep = ({ stepData, updateData }) => {
  const { t } = useLanguage();
  return (
    <motion.div 
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, type: 'spring' }}
      style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', paddingTop: '1rem' }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        <label style={{ fontWeight: 'bold', color: '#e2e8f0' }}>
          {t('pptx_notes')}
        </label>
        <textarea 
          rows="4"
          style={{ padding: '0.8rem', borderRadius: '12px', border: '1px solid rgba(99, 102, 241, 0.3)', outline: 'none', resize: 'vertical', fontFamily: 'inherit', background: 'rgba(30, 41, 59, 0.5)', color: '#fff' }} 
          placeholder={t('pptx_notesPlaceholder')}
          value={stepData.additional_notes || ''}
          onChange={(e) => updateData({ additional_notes: e.target.value })}
        />
      </div>
      <motion.div 
        animate={{ y: [0, -10, 0] }}
        transition={{ repeat: Infinity, duration: 3, ease: 'easeInOut' }}
        style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '1rem', marginTop: '1.5rem' }}
      >
        <div style={{ position: 'relative' }}>
          <FontAwesomeIcon icon={faRobot} size="4x" color="#a855f7" />
          <motion.div
             animate={{ rotate: 360 }}
             transition={{ repeat: Infinity, duration: 8, ease: 'linear' }}
             style={{ position: 'absolute', top: '-10px', right: '-10px', color: '#ec4899' }}
          >
            <FontAwesomeIcon icon={faStar} />
          </motion.div>
        </div>
        <h4 style={{ margin: 0, color: '#f8fafc', textShadow: '0 0 10px rgba(168, 85, 247, 0.6)' }}>
          {t('pptx_readyTitle')}
        </h4>
        <p style={{ margin: 0, color: '#94a3b8', textAlign: 'center', fontSize: '0.95rem' }}>
          {t('pptx_readyDesc')}
        </p>
      </motion.div>
    </motion.div>
  );
};

const StepperDemo = ({ useAIUI: propUseAIUI = false }) => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const useAIUI = location.state?.useAIUI ?? propUseAIUI;
  const [generatedPptxUrl, setGeneratedPptxUrl] = useState('');
  const [isPptxViewerOpen, setIsPptxViewerOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  const defaultSteps = [
    {
      id: 'step1_basic',
      title: t('pptx_basicInfo'),
      description: t('pptx_basicInfoDesc'),
      component: <BasicInfoStep />
    },
    {
      id: 'step2_config',
      title: t('pptx_config'),
      description: t('pptx_configDesc'),
      component: <ConfigStep />
    },
    {
      id: 'step3_confirm',
      title: t('pptx_confirm'),
      description: t('pptx_confirmDesc'),
      component: <ConfirmationStep />
    }
  ];

  const aiSteps = [
    {
      id: 'step1_basic',
      title: t('pptx_basicInfo'),
      description: t('pptx_basicInfoDesc'),
      component: <AIBasicInfoStep />
    },
    {
      id: 'step2_config',
      title: t('pptx_config'),
      description: t('pptx_configDesc'),
      component: <AIConfigStep />
    },
    {
      id: 'step3_confirm',
      title: t('pptx_confirm'),
      description: t('pptx_confirmDesc'),
      component: <AIConfirmationStep />
    }
  ];

  const steps = useAIUI ? aiSteps : defaultSteps;

  const handleComplete = async (formData) => {
    // Combine data from all steps
    const payload = {
      topic: formData.step1_basic?.topic || '',
      grade_level: formData.step1_basic?.grade_level || '',
      slides_count: formData.step2_config?.slides_count || 10,
      language: formData.step2_config?.language || 'ar',
      additional_notes: formData.step3_confirm?.additional_notes || '',
      presentation_style: {
        theme_name: "Theme 1",
        theme_mode: "Light",
        background_color: "#FFFFFF",
        text_color: "#000000",
        primary_accent_color: "#007ACC",
        secondary_accent_color: "#FFC107",
        title_font_family: "Arial",
        body_font_family: "Calibri",
        title_alignment: "Center",
        body_alignment: "Left",
        background_pattern: "Solid",
        shape_style: "Rounded",
        transition_type: "Fade"
      }
    };

    if (!payload.topic) {
      toastHelper.error(t('pptx_enterTopic'));
      return;
    }

    setIsGenerating(true);

    try {
      // The Stepper handles 'processing' UI but doesn't do API call if we process here 
      // instead of using finalApiEndpoint prop, but wait, DynamicStepper supports finalApiEndpoint.
      // However, we need to extract the url from response and show UI.
      // So let's call API manually here instead of relying on DynamicStepper's auto-call.
      
      const response = await ApiServices.post('teacher/mozakara/generatelesson', payload, false, 200000);
      const responseData = response?.data || response;
            
      if (responseData && responseData.error_message) {
          toastHelper.error(responseData.error_message);
      } else {
          const relativePath = responseData?.physical_path || responseData?.path || responseData?.url || (typeof responseData === 'string' ? responseData : '');
          
          if (relativePath) {
              const downloadUrl = getPublicUrlFromPhysicalPath(relativePath, AppKeys._MEDIA_URL_PRODUCTION);
              setGeneratedPptxUrl(downloadUrl);
              toastHelper.success(t('pptx_genSuccess'));
          } else {
              toastHelper.error(t('mz_aiPathNotFound'));
          }
      }
    } catch (error) {
      const errMsg = error?.response?.data?.error_message || error?.error_message || t('mz_aiFail');
      toastHelper.error(errMsg);
    } finally {
      setIsGenerating(false);
    }
  };

  const downloadPptx = () => {
    if (!generatedPptxUrl) return;
    const link = document.createElement("a");
    link.href = generatedPptxUrl;
    link.setAttribute("download", "");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className={`stepper-demo-container ${useAIUI ? 'stepper-ai-theme' : ''}`} style={{ background: useAIUI ? 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)' : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)' }}>
      
      {/* Back Button */}
      <div style={{ padding: '1rem', display: 'flex', justifyContent: 'flex-start' }}>
        <button 
          onClick={() => navigate(-1)}
          style={{ 
            background: 'none', 
            border: 'none', 
            cursor: 'pointer', 
            display: 'flex', 
            alignItems: 'center', 
            gap: '0.5rem', 
            color: useAIUI ? '#94a3b8' : '#64748b', 
            fontSize: '1.1rem',
            fontFamily: 'inherit'
          }}
        >
          <FontAwesomeIcon icon={language === 'ar' ? faArrowRight : faArrowLeft} />
          {t('examDetailsBack')}
        </button>
      </div>

      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        style={{ textAlign: 'center', marginBottom: '3rem' }}
      >
        <h1 className="stepper-demo-title" style={{ color: useAIUI ? '#f8fafc' : '#0f172a' }}>
          {t('pptx_title')}
          <span style={{ fontSize: '0.7rem', padding: '0.2rem 0.5rem', borderRadius: '12px', background: '#ec4899', color: '#fff', verticalAlign: 'middle', textShadow: 'none', fontWeight: 'bold' }}>
            {t('mz_aiBetaHint')}
          </span>
        </h1>
        <p className="stepper-demo-subtitle" style={{ color: useAIUI ? '#94a3b8' : '#64748b' }}>
          <FontAwesomeIcon icon={faInfoCircle} style={{ marginInlineEnd: '0.5rem', color: useAIUI ? '#a855f7' : 'inherit' }} />
          {t('pptx_subtitle')}
        </p>
      </motion.div>

      {generatedPptxUrl ? (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className={`stepper-success-card ${useAIUI ? 'stepper-ai-success-card' : ''}`}
        >
          <FontAwesomeIcon icon={faFilePowerpoint} size="4x" color="#f15b2a" style={{ marginBottom: '1rem' }} />
          <h2 style={{ color: useAIUI ? '#f8fafc' : '#0f172a', marginBottom: '1.5rem' }}>
            {t('pptx_readyHeader')}
          </h2>
          <div className="stepper-action-buttons">
            <button
              onClick={() => setIsPptxViewerOpen(true)}
              className="stepper-action-btn stepper-btn-view"
            >
              <FontAwesomeIcon icon={faEye} />
              {t('pptx_btnView')}
            </button>
            <button
              onClick={downloadPptx}
              className="stepper-action-btn stepper-btn-download"
            >
              <FontAwesomeIcon icon={faDownload} />
              {t('pptx_btnDownload')}
            </button>
          </div>
          <button
              onClick={() => setGeneratedPptxUrl('')}
              style={{ marginTop: '2rem', background: 'none', border: 'none', color: '#64748b', textDecoration: 'underline', cursor: 'pointer', fontFamily: 'inherit' }}
          >
              {t('pptx_btnAnother')}
          </button>
        </motion.div>
      ) : (
        <DynamicStepper 
          steps={steps}
          submissionMode="manual" // Setting to manual so onComplete gets called and we handle API manually to allow loading state while extracting payload
          onComplete={handleComplete}
          isAITheme={useAIUI}
          isExternalProcessing={isGenerating}
        />
      )}

      <PowerPointViewer
        isOpen={isPptxViewerOpen}
        onClose={() => setIsPptxViewerOpen(false)}
        fileUrl={generatedPptxUrl}
      />
    </div>
  );
};

export default StepperDemo;
